import { tv } from 'tailwind-variants'

import { StatusBadge, statusBadgeVariants } from '@/components/ui'
import { RECONCILIATION_STATUSES } from '@/modules/projects/constants'
import type { ReconciliationStatus } from '@/modules/projects/stores'

// Reconcile is its own axis, separate from the processing-status badge.
// UNKNOWN = a pre-engine statement we never checked.
const reconciliationBadgeVariants = tv({
  extend: statusBadgeVariants,
  slots: {
    base: 'whitespace-nowrap',
  },
  variants: {
    status: {
      [RECONCILIATION_STATUSES.reconciled]: {
        base: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
        dot: 'bg-emerald-500',
      },
      [RECONCILIATION_STATUSES.partiallyReconciled]: {
        base: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100',
        dot: 'bg-sky-500',
      },
      [RECONCILIATION_STATUSES.needsReview]: {
        base: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
        dot: 'bg-amber-500',
      },
      [RECONCILIATION_STATUSES.unverifiable]: {
        base: 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100',
        dot: 'bg-slate-400',
      },
      [RECONCILIATION_STATUSES.unknown]: {
        base: 'bg-neutral-100 text-neutral-500 border-neutral-200 hover:bg-neutral-150',
        dot: 'bg-neutral-400',
      },
      [RECONCILIATION_STATUSES.skipped]: {
        base: 'bg-neutral-100 text-neutral-500 border-neutral-200 hover:bg-neutral-150',
        dot: 'bg-neutral-400',
      },
    },
  },
  defaultVariants: {
    status: RECONCILIATION_STATUSES.unknown,
  },
})

export const RECONCILIATION_LABELS: Record<ReconciliationStatus, string> = {
  [RECONCILIATION_STATUSES.reconciled]: 'Reconciled',
  [RECONCILIATION_STATUSES.partiallyReconciled]: 'Partly checked',
  [RECONCILIATION_STATUSES.needsReview]: 'Needs review',
  [RECONCILIATION_STATUSES.unverifiable]: 'Unverifiable',
  [RECONCILIATION_STATUSES.unknown]: 'Not checked',
  [RECONCILIATION_STATUSES.skipped]: 'Skipped',
}

interface ReconciliationBadgeProps {
  status?: ReconciliationStatus
  // Optional suffix the caller sizes the verdict with, e.g. "7 of 12 checked".
  detail?: string
  className?: string
  showLabel?: boolean
}

export const ReconciliationBadge = ({
  status = RECONCILIATION_STATUSES.unknown,
  detail,
  className,
  showLabel = true,
}: ReconciliationBadgeProps) => (
  <StatusBadge
    slots={reconciliationBadgeVariants({ status })}
    label={
      <>
        {RECONCILIATION_LABELS[status]}
        {detail && <span className="opacity-70"> · {detail}</span>}
      </>
    }
    className={className}
    showLabel={showLabel}
  />
)
